import { useState, useEffect, FormEvent } from "react";
import { Link, Route, Routes, useLocation } from "react-router";
import { PriceChart, PriceChartSkeleton } from "./components/PriceChart";
import {
  PriceSummaryCards,
  PriceSummaryCardsSkeleton,
} from "./components/PriceSummaryCards";
import {
  HeaderControls,
  IntervalToggle,
} from "./components/Controls";
import { ProvidersTable } from "./components/ProvidersTable";
import {
  fetchPriceData,
  normalisePrices,
  PriceData,
} from "./services/priceApi";
import {
  fetchExpectedPrices,
  type ExpectedPrices,
} from "./services/expectedPriceApi";
import {
  COUNTRY_CONFIG,
  cheapestProviderId,
  providersForRegion,
  resolveSelectedProviderId,
} from "./config/countries";
import type { ResolvedProvider } from "./config/energyProvider";
import { readPrefsFromCookie, writePrefsToCookie } from "./utils/prefs";
import { formatDateLabel } from "./utils/format";
import { STRINGS } from "./i18n";
import {
  ArrowUpRight,
  ChevronLeft,
  ChevronRight,
  Clock3,
  LucideLightbulb,
  Menu,
  X,
} from "lucide-react";
import { AboutPage } from "./pages/About";
import { ApiPage } from "./pages/Api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

type PriceHistoryEntry = {
  data60m: Record<string, number>;
  data15m: Record<string, number>;
};

const PRICE_HISTORY_KEY = "price-history-v1";
const INCLUDE_TAXES = true;

const getDateKeyFromData = (data: Record<string, number> | undefined) => {
  if (!data) return null;
  const firstKey = Object.keys(data).sort()[0];
  return firstKey ? firstKey.slice(0, 10) : null;
};

const getAmsterdamDateKey = (date = new Date()) =>
  new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Amsterdam",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);

const getAmsterdamHour = (date = new Date()) =>
  Number(
    new Intl.DateTimeFormat("en-GB", {
      timeZone: "Europe/Amsterdam",
      hour: "2-digit",
      hour12: false,
    }).format(date)
  );

const addDaysToDateKey = (dateKey: string, days: number) => {
  const [year, month, day] = dateKey.split("-").map(Number);
  if (!year || !month || !day) return dateKey;
  const base = new Date(year, month - 1, day);
  return getAmsterdamDateKey(addDays(base, days));
};

const addDays = (date: Date, days: number) => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
};

function App() {
  const [regionId, setRegionId] = useState<string | null>(() => {
    if (!COUNTRY_CONFIG.regions) return null;
    const prefs = readPrefsFromCookie();
    const stored = prefs?.regionId;
    const valid = stored && COUNTRY_CONFIG.regions.some((r) => r.id === stored);
    return valid ? (stored as string) : COUNTRY_CONFIG.defaultRegionId;
  });
  const regionProviders: ResolvedProvider[] = providersForRegion(
    COUNTRY_CONFIG.providers,
    regionId
  );

  const [priceData, setPriceData] = useState<PriceData | null>(null);
  const [expectedPrices, setExpectedPrices] = useState<ExpectedPrices | null>(
    null
  );
  const [selectedProvider, setSelectedProvider] = useState<string>(() => {
    const prefs = readPrefsFromCookie();
    const id = prefs?.providerId;
    const initial = providersForRegion(COUNTRY_CONFIG.providers, regionId);
    const exists = id && initial.some((p) => p.id === id);
    return exists ? (id as string) : cheapestProviderId(initial) ?? "";
  });
  const effectiveProviderId = resolveSelectedProviderId(
    regionProviders,
    selectedProvider
  );
  const [interval, setInterval] = useState<"60m" | "15m">(() => {
    const prefs = readPrefsFromCookie();
    return prefs?.interval === "15m" ? "15m" : "60m";
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [priceHistory, setPriceHistory] = useState<Record<string, PriceHistoryEntry>>(
    () => {
      if (typeof window === "undefined") return {};
      const stored = window.localStorage.getItem(PRICE_HISTORY_KEY);
      if (!stored) return {};
      try {
        return JSON.parse(stored) as Record<string, PriceHistoryEntry>;
      } catch {
        return {};
      }
    }
  );
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [isCorrectionOpen, setIsCorrectionOpen] = useState(false);
  const [correctionEmail, setCorrectionEmail] = useState("");
  const [correctionMessage, setCorrectionMessage] = useState("");
  const [correctionStatus, setCorrectionStatus] = useState<"idle" | "sending" | "success" | "error">(
    "idle"
  );
  const [correctionError, setCorrectionError] = useState<string | null>(null);
  const [chartError, setChartError] = useState<string | null>(null);
  const [loadingDate, setLoadingDate] = useState<string | null>(null);
  const [showTomorrowInfo, setShowTomorrowInfo] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const loadPriceData = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await fetchPriceData();
        setPriceData(data);
      } catch (err) {
        setError(STRINGS.app.loadError);
        console.error("Error loading price data:", err);
      } finally {
        setLoading(false);
      }
    };

    loadPriceData();
  }, []);

  useEffect(() => {
    if (!COUNTRY_CONFIG.expectedPriceSource) return;
    // Without expected prices the provider table falls back to the selected day's average.
    fetchExpectedPrices()
      .then(setExpectedPrices)
      .catch((err) => console.error("Error loading expected prices:", err));
  }, []);

  useEffect(() => {
    if (!priceData) return;

    const todayKey = getDateKeyFromData(priceData.today_60m);
    const tomorrowKey = getDateKeyFromData(priceData.tomorrow_60m);
    const expectedTomorrowKey = getAmsterdamDateKey(addDays(currentTime, 1));

    setPriceHistory((prev) => {
      let changed = false;
      const next = { ...prev };
      if (todayKey) {
        const existing = prev[todayKey];
        if (
          !existing ||
          existing.data60m !== priceData.today_60m ||
          existing.data15m !== priceData.today_15m
        ) {
          next[todayKey] = {
            data60m: priceData.today_60m,
            data15m: priceData.today_15m,
          };
          changed = true;
        }
      }
      if (tomorrowKey && priceData.tomorrow_60m && priceData.tomorrow_15m) {
        const existing = prev[tomorrowKey];
        if (
          !existing ||
          existing.data60m !== priceData.tomorrow_60m ||
          existing.data15m !== priceData.tomorrow_15m
        ) {
          next[tomorrowKey] = {
            data60m: priceData.tomorrow_60m,
            data15m: priceData.tomorrow_15m,
          };
          changed = true;
        }
      } else if (expectedTomorrowKey && next[expectedTomorrowKey]) {
        delete next[expectedTomorrowKey];
        changed = true;
      }
      return changed ? next : prev;
    });

    if (!selectedDate && todayKey) {
      setSelectedDate(todayKey);
    }
  }, [priceData, selectedDate, currentTime]);

  // Persist user preferences to a cookie whenever they change
  useEffect(() => {
    writePrefsToCookie({
      providerId: effectiveProviderId,
      includeTaxes: INCLUDE_TAXES,
      interval,
      ...(regionId ? { regionId } : {}),
    });
  }, [effectiveProviderId, interval, regionId]);

  // Strict regiofilter: de effectieve selectie (goedkoopste beschikbare
  // leverancier als de huidige selectie ongeldig is voor de regio) wordt
  // hier alleen teruggeschreven naar state zodat vervolgrenders/de gebruikerskeuze
  // consistent blijven; render zelf gebruikt al effectiveProviderId.
  useEffect(() => {
    if (effectiveProviderId && effectiveProviderId !== selectedProvider) {
      setSelectedProvider(effectiveProviderId);
    }
  }, [effectiveProviderId, selectedProvider]);

  useEffect(() => {
    let intervalId: number | null = null;

    const updateTime = () => setCurrentTime(new Date());
    const now = new Date();
    const msUntilNextMinute =
      (60 - now.getSeconds()) * 1000 - now.getMilliseconds();

    const timeoutId = window.setTimeout(() => {
      updateTime();
      intervalId = window.setInterval(updateTime, 60000);
    }, msUntilNextMinute);

    return () => {
      window.clearTimeout(timeoutId);
      if (intervalId !== null) {
        window.clearInterval(intervalId);
      }
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(PRICE_HISTORY_KEY, JSON.stringify(priceHistory));
  }, [priceHistory]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("correctie") === "1") {
      setIsCorrectionOpen(true);
      setCorrectionStatus("idle");
      setCorrectionError(null);
      params.delete("correctie");
      const nextQuery = params.toString();
      const nextUrl = nextQuery ? `${location.pathname}?${nextQuery}` : location.pathname;
      window.history.replaceState({}, "", nextUrl);
    }
  }, [location.pathname, location.search]);

  const selectedProviderData = regionProviders.find(
    (p) => p.id === effectiveProviderId
  );
  const providerCharge = selectedProviderData?.perKwhChargeExcludingVat || 0;

  const selectedEntry =
    selectedDate && priceHistory[selectedDate]
      ? priceHistory[selectedDate]
      : undefined;
  const chartData = selectedEntry
    ? interval === "60m"
      ? selectedEntry.data60m
      : selectedEntry.data15m
    : undefined;
  const chartPrices = chartData ? Object.values(chartData) : [];
  const averageSpotPrice = chartPrices.length
    ? chartPrices.reduce((total, price) => total + price, 0) /
      chartPrices.length
    : undefined;
  const selectedPriceDate = selectedDate
    ? new Date(`${selectedDate}T12:00:00`)
    : currentTime;

  const todayKey = getAmsterdamDateKey(currentTime);
  const tomorrowKey = getAmsterdamDateKey(addDays(currentTime, 1));
  const nextDate = selectedDate ? addDaysToDateKey(selectedDate, 1) : null;
  const hasTomorrowData = !!(tomorrowKey && priceHistory[tomorrowKey]);
  const canShowNext =
    !!selectedDate &&
    (selectedDate < todayKey ||
      (selectedDate === todayKey && hasTomorrowData)) &&
    selectedDate !== tomorrowKey;
  const currentAmsterdamHour = getAmsterdamHour(currentTime);
  const showTomorrowClock =
    selectedDate === todayKey &&
    !hasTomorrowData &&
    currentAmsterdamHour < 15;
  const showTomorrowWarning = !hasTomorrowData && currentAmsterdamHour >= 15;
  const isSelectedDateLoading =
    !!selectedDate && loadingDate === selectedDate && !chartData;
  const selectedDateHasNoData =
    !loading &&
    !error &&
    !!selectedDate &&
    !chartData &&
    !chartError &&
    !isSelectedDateLoading;
  const missingDataNotices: Array<{
    id: string;
    title: string;
    description: string;
    closable: boolean;
  }> = [];

  if (error) {
    missingDataNotices.push({
      id: "load-error",
      title: STRINGS.app.pricesUnavailableTitle,
      description: error,
      closable: false,
    });
  } else if (chartError) {
    missingDataNotices.push({
      id: "date-error",
      title: STRINGS.app.pricesUnavailableTitle,
      description: chartError,
      closable: false,
    });
  } else if (selectedDateHasNoData) {
    missingDataNotices.push({
      id: "missing-date",
      title: STRINGS.app.pricesUnavailableTitle,
      description: STRINGS.app.noDataForDate,
      closable: false,
    });
  }

  if (showTomorrowWarning) {
    missingDataNotices.push({
      id: "tomorrow-delayed",
      title: STRINGS.app.tomorrowDelayedTitle,
      description: STRINGS.app.tomorrowDelayedBody,
      closable: false,
    });
  }

  if (showTomorrowInfo && showTomorrowClock) {
    missingDataNotices.push({
      id: "tomorrow-info",
      title: STRINGS.app.tomorrowPendingTitle,
      description: STRINGS.app.tomorrowPendingBody,
      closable: true,
    });
  }
  const isSelectedToday = selectedDate === todayKey;
  const selectedDateLabel = selectedDate
    ? isSelectedToday
      ? STRINGS.today
      : formatDateLabel(selectedDate)
    : "";
  const currentTimeLabel = isSelectedToday
    ? STRINGS.app.nowLabel
    : STRINGS.app.currentTimeLabel;

  useEffect(() => {
    if (chartData) {
      setChartError(null);
    }
  }, [chartData]);

  const loadDateData = async (dateKey: string) => {
    setChartError(null);
    setLoadingDate(dateKey);
    try {
      const response = await fetch(`/api/prices/${dateKey}`);
      if (!response.ok) {
        throw new Error(`Request failed: ${response.status}`);
      }
      const payload = await response.json();
      if (!payload?.today || !Array.isArray(payload.today)) {
        throw new Error("Missing price data");
      }
      const normalized = normalisePrices(payload.today);
      const normalizedDateKey = getDateKeyFromData(normalized.today_60m);
      if (!normalizedDateKey || normalizedDateKey !== dateKey) {
        const todayKey = getAmsterdamDateKey(currentTime);
        const tomorrowKey = getAmsterdamDateKey(addDays(currentTime, 1));
        setPriceHistory((prev) => {
          if (!prev[dateKey]) return prev;
          const next = { ...prev };
          delete next[dateKey];
          return next;
        });
        if (dateKey === tomorrowKey && dateKey > todayKey) {
          setChartError(STRINGS.app.tomorrowNotAvailable);
        } else {
          setChartError(STRINGS.app.dateLoadError);
        }
        return false;
      }
      setPriceHistory((prev) => ({
        ...prev,
        [dateKey]: {
          data60m: normalized.today_60m,
          data15m: normalized.today_15m,
        },
      }));
      setChartError(null);
      return true;
    } catch (err) {
      console.error("Failed to load date prices", err);
      const todayKey = getAmsterdamDateKey(currentTime);
      const tomorrowKey = getAmsterdamDateKey(addDays(currentTime, 1));
      if (dateKey === tomorrowKey && dateKey > todayKey) {
        setChartError(STRINGS.app.tomorrowNotAvailable);
      } else {
        setChartError(STRINGS.app.dateLoadError);
      }
      return false;
    } finally {
      setLoadingDate((current) => (current === dateKey ? null : current));
    }
  };

  const submitCorrection = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!correctionMessage.trim()) return;
    setCorrectionStatus("sending");
    setCorrectionError(null);

    try {
      const response = await fetch("/api/correction", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: correctionEmail.trim() || null,
          message: correctionMessage.trim(),
          page: window.location.href,
        }),
      });

      if (!response.ok) {
        throw new Error(`Request failed: ${response.status}`);
      }

      setCorrectionStatus("success");
      setCorrectionMessage("");
      setCorrectionEmail("");
    } catch (err) {
      console.error("Correction submit failed", err);
      setCorrectionStatus("error");
      setCorrectionError(STRINGS.correction.sendError);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border bg-card shadow-sm">
        <div className="mx-auto flex max-w-(--breakpoint-2xl) items-center justify-between gap-3 px-4 py-4 sm:gap-6 sm:px-6 sm:py-6 lg:px-8">
          <Link to="/" className="flex min-w-0 items-center gap-2 text-inherit sm:gap-3">
            <LucideLightbulb className="h-7 w-7 shrink-0 text-primary sm:h-8 sm:w-8" />
            <div className="min-w-0">
              <h1 className="truncate text-xl font-bold text-foreground sm:text-3xl">
                {COUNTRY_CONFIG.domain}
              </h1>
              <p className="hidden text-sm text-muted-foreground sm:block">
                {COUNTRY_CONFIG.siteTagline}
              </p>
            </div>
          </Link>
          <nav className="flex shrink-0 items-center gap-2 text-sm font-medium text-muted-foreground sm:gap-6">
            <div className="hidden items-center gap-6 xl:flex">
              <Link
                to="/about"
                className="transition-colors hover:text-foreground"
              >
                {STRINGS.app.navAbout}
              </Link>
              <Link
                to="/api-documentatie"
                className="transition-colors hover:text-foreground"
              >
                {STRINGS.app.navApi}
              </Link>
            </div>
            <Popover open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <PopoverTrigger asChild className="xl:hidden">
                <Button variant="ghost" size="icon" aria-label={STRINGS.app.openMenu}>
                  <Menu className="h-5 w-5" />
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-48 p-2">
                <div className="flex flex-col text-sm font-medium">
                  <Link
                    to="/about"
                    onClick={() => setMobileMenuOpen(false)}
                    className="rounded-md px-3 py-2 transition-colors hover:bg-accent hover:text-accent-foreground"
                  >
                    {STRINGS.app.navAbout}
                  </Link>
                  <Link
                    to="/api-documentatie"
                    onClick={() => setMobileMenuOpen(false)}
                    className="rounded-md px-3 py-2 transition-colors hover:bg-accent hover:text-accent-foreground"
                  >
                    {STRINGS.app.navApi}
                  </Link>
                </div>
              </PopoverContent>
            </Popover>
          </nav>
        </div>
        {location.pathname === "/" && (
          <HeaderControls
            providers={regionProviders}
            selectedProvider={effectiveProviderId}
            onProviderChange={setSelectedProvider}
            includeTaxes={INCLUDE_TAXES}
            regions={COUNTRY_CONFIG.regions}
            regionId={regionId}
            onRegionChange={setRegionId}
          />
        )}
      </header>

      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <Routes>
          <Route
            path="/"
            element={
              <>
                {loading && (
                  <div className="flex items-center justify-center py-12">
                    <div className="text-center">
                      <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-b-2 border-primary"></div>
                      <p className="text-muted-foreground">
                        {STRINGS.app.loadingPrices}
                      </p>
                    </div>
                  </div>
                )}

                {!loading && missingDataNotices.length > 0 && (
                  <div className="mb-6 space-y-3">
                    {missingDataNotices.map((notice) => (
                      <Alert
                        key={notice.id}
                        variant="warning"
                        className={notice.closable ? "pr-12" : undefined}
                      >
                        <Clock3 aria-hidden="true" />
                        <AlertTitle>{notice.title}</AlertTitle>
                        <AlertDescription>{notice.description}</AlertDescription>
                        {notice.closable && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => setShowTomorrowInfo(false)}
                            aria-label={STRINGS.app.closeNotice}
                            className="absolute right-4 top-3 h-8 w-8 text-amber-800 hover:bg-amber-100 hover:text-amber-950"
                          >
                            <X aria-hidden="true" />
                          </Button>
                        )}
                      </Alert>
                    ))}
                  </div>
                )}

                {!loading && (
                  <>
                    {isSelectedDateLoading && (
                      <PriceSummaryCardsSkeleton
                        showCurrentPrice={selectedDate === todayKey}
                      />
                    )}
                    {chartData && (
                      <PriceSummaryCards
                        data={chartData}
                        interval={interval}
                        dateLabel={selectedDateLabel}
                        providerPerKwhCharge={providerCharge}
                        providerSpotCoefficient={
                          selectedProviderData?.spotCoefficient
                        }
                        includeTaxes={INCLUDE_TAXES}
                        currentTime={currentTime}
                        regionId={regionId}
                      />
                    )}

                    <Card
                      className="mb-8 overflow-hidden"
                      aria-busy={isSelectedDateLoading}
                    >
                      <div
                        role="group"
                        aria-label={STRINGS.app.chartControls}
                        className="grid grid-cols-[2.5rem_minmax(0,1fr)_2.5rem] items-center gap-x-2 gap-y-3 border-b border-border bg-muted/30 px-3 py-3 sm:px-6 lg:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] lg:gap-x-3"
                      >
                        <div className="contents lg:col-start-2 lg:flex lg:items-center lg:justify-center lg:gap-3">
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            className="h-10 w-10 lg:h-9 lg:w-9"
                            onClick={() => {
                              const targetDate = selectedDate
                                ? addDaysToDateKey(selectedDate, -1)
                                : getAmsterdamDateKey(addDays(new Date(), -1));
                              setSelectedDate(targetDate);
                              if (!priceHistory[targetDate]) {
                                void loadDateData(targetDate);
                              }
                            }}
                            aria-label={STRINGS.app.previousDay}
                          >
                            <ChevronLeft className="h-4 w-4" />
                          </Button>
                          <h2 className="min-w-0 text-center leading-tight text-foreground">
                            <span className="block text-[0.625rem] font-semibold uppercase tracking-wider text-muted-foreground sm:hidden">
                              {STRINGS.app.chartTitle}
                            </span>
                            <span className="mt-0.5 block text-sm font-semibold sm:hidden">
                              {selectedDateLabel}
                            </span>
                            <span className="hidden text-lg font-semibold sm:inline">
                              {STRINGS.app.chartHeading(
                                selectedDateLabel,
                                isSelectedToday
                              )}
                            </span>
                          </h2>
                          <div className="contents lg:flex lg:items-center lg:text-sm">
                            {showTomorrowClock ? (
                              <TooltipProvider delayDuration={300}>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      type="button"
                                      variant="outline"
                                      size="icon"
                                      className="h-10 w-10 lg:h-9 lg:w-9"
                                      onClick={() => setShowTomorrowInfo(true)}
                                      aria-label={STRINGS.app.tomorrowPendingTitle}
                                    >
                                      <Clock3 aria-hidden="true" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent className="whitespace-nowrap">
                                    {STRINGS.app.tomorrowPendingTitle}
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            ) : (
                              <Button
                                type="button"
                                variant="outline"
                                size="icon"
                                className="h-10 w-10 lg:h-9 lg:w-9"
                                onClick={() => {
                                  if (
                                    !selectedDate ||
                                    !nextDate ||
                                    !canShowNext
                                  ) {
                                    return;
                                  }
                                  setSelectedDate(nextDate);
                                  setShowTomorrowInfo(false);
                                  if (!priceHistory[nextDate]) {
                                    void loadDateData(nextDate);
                                  }
                                }}
                                aria-label={STRINGS.app.nextDay}
                                disabled={!canShowNext}
                              >
                                <ChevronRight className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                        <div className="col-span-3 justify-self-center lg:col-span-1 lg:col-start-3 lg:justify-self-end">
                          <IntervalToggle
                            interval={interval}
                            onIntervalChange={setInterval}
                          />
                        </div>
                      </div>
                      {isSelectedDateLoading && <PriceChartSkeleton />}
                      {chartData && (
                        <PriceChart
                          data={chartData}
                          interval={interval}
                          providerPerKwhCharge={providerCharge}
                          includeTaxes={INCLUDE_TAXES}
                          currentTime={currentTime}
                          currentTimeLabel={currentTimeLabel}
                          providerSpotCoefficient={selectedProviderData?.spotCoefficient}
                          regionId={regionId}
                        />
                      )}
                    </Card>

                    <Card className="mb-8">
                      <CardContent className="px-6 py-5 text-sm leading-relaxed text-foreground">
                        <p>{STRINGS.app.explainer}</p>
                      </CardContent>
                    </Card>

                    <ProvidersTable
                      includeTaxes={INCLUDE_TAXES}
                      selectedProvider={effectiveProviderId}
                      providers={regionProviders}
                      averageSpotPrice={averageSpotPrice}
                      expectedPrices={expectedPrices ?? undefined}
                      priceDate={selectedPriceDate}
                      regionId={regionId}
                    />

                    <Card className="mt-8 mb-8">
                      <CardContent className="flex flex-col items-start gap-4 px-6 py-5 text-sm leading-relaxed text-foreground sm:flex-row sm:items-center sm:justify-between">
                        <p>{STRINGS.app.batteryCtaQuestion}</p>
                        <Button asChild className="shrink-0">
                          <a
                            href={COUNTRY_CONFIG.ctaUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {STRINGS.app.batteryCtaLink(COUNTRY_CONFIG.ctaName)}
                            <ArrowUpRight />
                          </a>
                        </Button>
                      </CardContent>
                    </Card>
                  </>
                )}
              </>
            }
          />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/api-documentatie" element={<ApiPage />} />
        </Routes>
      </main>

      <footer className="mt-12 border-t border-border bg-card">
        <div className="mx-auto max-w-7xl px-4 py-6 text-center text-sm text-muted-foreground sm:px-6 lg:px-8">
          <p>{STRINGS.app.footerUpdates}</p>
          <p className="mt-2 text-xs">
            {STRINGS.app.footerInitiative}{" "}
            <a
              href={COUNTRY_CONFIG.ctaUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-foreground"
            >
              {COUNTRY_CONFIG.ctaName}
            </a>
          </p>
        </div>
      </footer>

      <Dialog open={isCorrectionOpen} onOpenChange={setIsCorrectionOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{STRINGS.correction.title}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitCorrection} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="correction-email">
                {STRINGS.correction.emailLabel}
              </Label>
              <Input
                id="correction-email"
                name="correction-email"
                type="email"
                autoComplete="email"
                spellCheck={false}
                value={correctionEmail}
                onChange={(event) => setCorrectionEmail(event.target.value)}
                placeholder={STRINGS.correction.emailPlaceholder}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="correction-message">
                {STRINGS.correction.messageLabel}
              </Label>
              <Textarea
                id="correction-message"
                name="correction-message"
                autoComplete="off"
                value={correctionMessage}
                onChange={(event) => setCorrectionMessage(event.target.value)}
                rows={5}
                placeholder={STRINGS.correction.messagePlaceholder}
                required
              />
            </div>
            <div aria-live="polite" className="empty:hidden">
              {correctionStatus === "error" && correctionError && (
                <p className="text-sm text-destructive">{correctionError}</p>
              )}
              {correctionStatus === "success" && (
                <p className="text-sm text-emerald-600">
                  {STRINGS.correction.success}
                </p>
              )}
            </div>
            <DialogFooter className="gap-2 sm:gap-2">
              <DialogClose asChild>
                <Button type="button" variant="ghost">
                  {STRINGS.correction.close}
                </Button>
              </DialogClose>
              <Button type="submit" disabled={correctionStatus === "sending"}>
                {correctionStatus === "sending"
                  ? STRINGS.correction.sending
                  : STRINGS.correction.send}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default App;
